import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiShoppingBag, FiArrowRight } from 'react-icons/fi';
import http from '../../api/http';
import { initTbankPayment, ensureCsrf } from '../../api/orders';
import { labelCustomerOrder } from '../../utils/orderStatus';
import './Account.css';

export default function AccountOrders() {
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [busyId, setBusyId] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const { data } = await http.get('/api/orders', { params: { mine: 1 } });
      setOrders(Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : []);
    } catch {
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const canPay = (o) =>
    o.status === 'reserved' &&
    ['unpaid', 'pending'].includes(o.payment?.status) &&
    !(o.reserveExpiresAt && new Date(o.reserveExpiresAt) <= new Date());

  async function pay(orderId, { force = false } = {}) {
    setBusyId(orderId);
    try {
      await ensureCsrf();
      const res = await initTbankPayment(orderId, { force });
      if (res?.paymentUrl) {
        window.location.href = res.paymentUrl;
        return;
      }
      alert('Не удалось получить ссылку на оплату');
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || 'Ошибка оплаты';
      const retry = window.confirm(`${msg}\n\nПопробовать новую ссылку?`);
      if (retry && !force) return pay(orderId, { force: true });
      await load();
    } finally {
      setBusyId(null);
    }
  }

  if (loading) {
    return (
      <div className="account-state">
        <div className="account-skeleton" />
        <div className="account-skeleton account-skeleton--short" />
      </div>
    );
  }

  if (!orders.length) {
    return (
      <section className="account-card account-empty">
        <div className="account-empty__icon" aria-hidden>
          <FiShoppingBag />
        </div>
        <h2 className="account-card__title">Заказов пока нет</h2>
        <p className="account-card__hint">Соберите корзину в каталоге — история появится здесь.</p>
        <Link to="/catalog" className="account-btn">
          В каталог <FiArrowRight />
        </Link>
      </section>
    );
  }

  return (
    <section className="account-card">
      <div className="account-card__head">
        <h2 className="account-card__title">История заказов</h2>
        <p className="account-card__hint">Отмена заказа — через менеджера.</p>
      </div>

      <div className="orders">
        {orders.map((o) => {
          const statusKey = statusTone(o);
          return (
            <article key={o._id || o.id} className="order">
              <div className="order__head">
                <div>
                  <div className="order__id">Заказ №{o.number || String(o._id || '').slice(-6)}</div>
                  <div className="order__date">{fmtDate(o.createdAt || o.date)}</div>
                </div>
                <div className="order__sum">{fmtSum(o.total || o.amount || 0)} ₽</div>
                <div className={`order__status order__status--${statusKey}`}>
                  {labelCustomerOrder(o)}
                </div>
              </div>

              {Array.isArray(o.items) && o.items.length > 0 && (
                <ul className="order__items">
                  {o.items.map((it, i) => (
                    <li key={i}>
                      <span className="order__qty">×{it.qty || 1}</span>
                      <span className="order__title">
                        {it.title || it.product?.title || 'Товар'}
                      </span>
                      <span className="order__price">
                        {fmtSum(it.price || it.product?.price?.effective || 0)} ₽
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              {canPay(o) && (
                <div className="order__actions">
                  <button
                    type="button"
                    className="account-btn"
                    disabled={busyId === o._id}
                    onClick={() => pay(o._id)}
                  >
                    {busyId === o._id
                      ? '…'
                      : o.payment?.status === 'pending'
                        ? 'Продолжить оплату'
                        : 'Оплатить'}
                  </button>
                  {o.payment?.status === 'pending' && (
                    <button
                      type="button"
                      className="account-btn account-btn--ghost"
                      disabled={busyId === o._id}
                      onClick={() => pay(o._id, { force: true })}
                    >
                      Новая ссылка
                    </button>
                  )}
                </div>
              )}

              <p className="order__legal" style={{ marginTop: 10, fontSize: 13, opacity: 0.85 }}>
                <a href="/return-memo" target="_blank" rel="noreferrer">
                  Памятка о возврате
                </a>
                {' · '}
                <a href="/delivery" target="_blank" rel="noreferrer">
                  Доставка и возврат
                </a>
              </p>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function statusTone(o) {
  const s = String(o.status || '').toLowerCase();
  if (['delivered', 'completed', 'done'].includes(s)) return 'ok';
  if (['canceled', 'cancelled', 'expired'].includes(s)) return 'bad';
  if (['paid', 'processing', 'reserved', 'shipping'].includes(s)) return 'warn';
  return 'muted';
}

function fmtDate(d) {
  try {
    return new Date(d).toLocaleString('ru-RU');
  } catch {
    return '';
  }
}

function fmtSum(n) {
  return Number(n || 0).toLocaleString('ru-RU');
}
